// P0 인증 fail-closed(docs/p0-auth-fail-closed-spec.md §3~§6) — /api/auth-verify bounded 검증 클라이언트.
// 순수 함수(fetch 주입) — node 회귀 테스트가 직접 호출한다(tests/p0-auth-fail-closed.test.mjs).
//  - 채택 조건: HTTP ok + success:true + non-empty user.email + normalized email 이 로컬 payload email 과 일치.
//  - 그 외(네트워크 예외·timeout/abort·non-2xx·malformed JSON·success:false·principal 결손/불일치)는 전부
//    { ok:false, code } — code 는 고정 3종뿐. throw 하지 않고, 원시 오류 문자열(AbortError·TypeError·파서 메시지)을
//    결과에 싣지 않는다.
//  - AbortController 로 유한 대기(AUTH_VERIFY_TIMEOUT_MS). 호출부 cleanup 용 외부 signal 을 받으면 함께 abort 한다.
//  - 요청 본문은 기존 계약 그대로 { credential } 1개 — 개인정보 필드를 추가하지 않는다.
export const AUTH_VERIFY_TIMEOUT_MS = 10_000;

export const AUTH_FAIL = Object.freeze({
  REJECTED: "AUTH_VERIFY_REJECTED", // 서버가 credential 을 거부(4xx·success:false) 또는 검증 불가 입력
  UNAVAILABLE: "AUTH_VERIFY_UNAVAILABLE", // 네트워크 예외·timeout·abort·5xx
  INVALID: "AUTH_RESPONSE_INVALID", // 2xx 인데 본문/principal 이 계약 밖(malformed JSON·user 결손·email 불일치)
});

export function normalizeEmail(v) {
  return typeof v === "string" ? v.trim().toLowerCase() : "";
}

// Session tranche A(§A-3) principal 결속: 재발급 credential 은 "서버가 검증했다"만으로 채택하지 않는다.
//  지금 로그인된(서버 검증된) principal 과 같은 계정일 때만 true. 어느 한쪽이라도 비어 있으면 false —
//  principal 미지정이 계정 전환의 통과 근거가 되지 않는다(fail-closed).
export function isSamePrincipal(current, candidate) {
  const a = normalizeEmail(current);
  const b = normalizeEmail(candidate);
  return !!a && a === b;
}

const fail = (code) => ({ ok: false, code });

/**
 * @param {string} credential Google ID token(GSI credential)
 * @param {{ expectedEmail?: string, fetchImpl?: typeof fetch, timeoutMs?: number, signal?: AbortSignal }} [opts]
 * @returns {Promise<{ ok: true, user: { email: string, name: string, picture: string } } | { ok: false, code: string }>}
 */
export async function verifyCredentialWithServer(
  credential,
  { expectedEmail, fetchImpl, timeoutMs = AUTH_VERIFY_TIMEOUT_MS, signal } = {},
) {
  const expected = normalizeEmail(expectedEmail);
  if (typeof credential !== "string" || !credential || !expected)
    return fail(AUTH_FAIL.REJECTED);
  if (signal?.aborted) return fail(AUTH_FAIL.UNAVAILABLE);

  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, timeoutMs);
  const doFetch = fetchImpl || ((...args) => globalThis.fetch(...args));
  try {
    let resp;
    try {
      resp = await doFetch("/api/auth-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
        signal: controller.signal,
      });
    } catch {
      return fail(AUTH_FAIL.UNAVAILABLE);
    }
    if (!resp || typeof resp.ok !== "boolean") return fail(AUTH_FAIL.INVALID);
    if (!resp.ok)
      return fail(
        resp.status >= 500 ? AUTH_FAIL.UNAVAILABLE : AUTH_FAIL.REJECTED,
      );
    // auth-verify의 유일한 성공 계약은 200이다. 예상 밖 2xx를 성공으로 확대하지 않는다.
    if (resp.status !== 200) return fail(AUTH_FAIL.INVALID);
    let data;
    try {
      data = await resp.json();
    } catch {
      return fail(
        controller.signal.aborted ? AUTH_FAIL.UNAVAILABLE : AUTH_FAIL.INVALID,
      );
    }
    if (!data || typeof data !== "object" || Array.isArray(data))
      return fail(AUTH_FAIL.INVALID);
    if (data.success !== true) return fail(AUTH_FAIL.REJECTED);
    const user = data.user;
    const email = normalizeEmail(user?.email);
    if (!email || email !== expected) return fail(AUTH_FAIL.INVALID);
    return {
      ok: true,
      ...(Number.isFinite(data.session?.expiresAt) ? { sessionExpiresAt: data.session.expiresAt } : {}),
      user: {
        email: user.email,
        name: typeof user.name === "string" ? user.name : "",
        picture: typeof user.picture === "string" ? user.picture : "",
      },
    };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

// P0 인증 fail-closed(§6) 인증 작업 세대 가드(순수 — node 회귀 테스트가 직접 호출). AuthProvider 가 ref 에 1개 보관.
//  - begin(): 새 인증 작업(복원·login·reauth·expired refresh) 시작 → 이전 세대 전부 stale. 새 세대 번호 반환.
//  - invalidate(): 명시적 logout → 진행 중 세대 전부 stale.
//  - mount()/unmount(): provider mount 상태. unmount 는 세대도 올려 이후 어떤 완료도 current 가 아니다
//    (StrictMode 1차 effect 의 완료 포함). 재mount 뒤 begin() 한 새 세대만 current.
//  - isCurrent(gen): mounted 이고 gen 이 최신일 때만 true — await 뒤 storage/state/session-expired/Sentry 쓰기 직전에
//    호출한다. false 면 호출부는 아무것도 쓰지 않고 고정 code/오류로 끝난다.
export function createAuthOpGuard() {
  let gen = 0;
  let mounted = false;
  return {
    begin: () => ++gen,
    invalidate: () => {
      gen += 1;
    },
    mount: () => {
      mounted = true;
    },
    unmount: () => {
      mounted = false;
      gen += 1;
    },
    isCurrent: (g) => mounted && g === gen,
  };
}

// The HttpOnly cookie stays inside the browser. Only server-verified identity/expiry are returned.
export async function readAppSession({ signal, fetchImpl = fetch } = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) return { ok: false };
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, AUTH_VERIFY_TIMEOUT_MS);
  try {
    const response = await fetchImpl("/api/session", { credentials: "same-origin", cache: "no-store", signal: controller.signal });
    if (response.status !== 200) return { ok: false, unavailable: response.status >= 500 };
    const value = await response.json();
    if (value?.active === false) return { ok: true, active: false };
    if (value?.active !== true || !/^[^@\s]+@opinno\.co\.kr$/.test(value.user?.email || "") ||
        !Number.isFinite(value.expiresAt) || value.expiresAt <= Date.now() ||
        value.expiresAt > Date.now() + 7 * 24 * 60 * 60 * 1000) return { ok: false };
    return { ok: true, active: true, user: { email: value.user.email,
      name: typeof value.user.name === "string" ? value.user.name : "",
      picture: typeof value.user.picture === "string" ? value.user.picture : "" }, expiresAt: value.expiresAt };
  } catch { return { ok: false, unavailable: true }; }
  finally { clearTimeout(timer); signal?.removeEventListener("abort", abort); }
}
